/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import React, {useEffect, useMemo, useState} from 'react';
import DvrdSelect from "./dvrdSelect";
import {ChangeFunction, ErrorType, SelectItemShape} from "../util/interfaces";
import {stringContains} from '../util/controlUtil';
import DvrdMultiSelect from "./dvrdMultiSelect";

interface Props<ValueType = string | number | Array<string | number>> {
    onChange: ChangeFunction<ValueType>;
    value: ValueType;
    items: Array<SelectItemShape<ValueType>>;
    disabled?: boolean;
    label?: string;
    error?: ErrorType;
    className?: string;
    labelClassName?: string;
    valueClassName?: string;
    arrowClassName?: string;
    errorClassName?: string;
    itemContainerClassName?: string;
    itemClassName?: string;
    itemsPosition?: 'top' | 'bottom';
    selectOnly?: false;
    optionsContainerHeight?: number | string;
    unControlled?: true;
    multi?: true;
    placeholder?: string;
    submitOnClose?: boolean;
}

export default function DvrdSelectController<ValueType = string | number | Array<string | number>>(props: Props<ValueType>) {
    const {
        value, multi, unControlled, onChange, items, error, className, labelClassName, valueClassName,
        itemContainerClassName, arrowClassName, errorClassName, itemClassName, label, disabled, placeholder,
        submitOnClose
    } = props;
    const [search, setSearch] = useState('');
    const [internalValue, setInternalValue] = useState<ValueType>('' as ValueType);
    const _items = useMemo(() => {
        if (!search) return items;
        return items.filter((item) => stringContains(item.label.toString(), search));
    }, [items, search]);

    function _onChange(value: ValueType, submit: boolean = true) {
        return function() {
            if (submit) onChange(value);
            setInternalValue(value);
        }
    }

    function onChangeSearch(evt: React.ChangeEvent<HTMLInputElement>) {
        setSearch(evt.target.value);
    }

    useEffect(() => {
        if (multi && !Array.isArray(value)) throw new TypeError('Value must be an array in multi mode');
        else if (!multi && Array.isArray(value)) throw new TypeError('Value must be a string or number in single mode');
    }, [value, multi]);

    useEffect(() => {
        if (!unControlled && value !== internalValue) setInternalValue(value);
    }, [value, unControlled]);

    const itemsPosition = props.itemsPosition ?? 'bottom';
    const optionsContainerHeight = props.optionsContainerHeight ?? '15rem';
    const selectOnly = props.selectOnly !== false;
    if (multi) return (
        <DvrdMultiSelect onChange={_onChange as any} onChangeSearch={onChangeSearch}
                         selected={internalValue as Array<string | number>}
                         items={_items as Array<SelectItemShape<number | string>>} itemsPosition={itemsPosition}
                         selectOnly={selectOnly} searchValue={search} optionsContainerHeight={optionsContainerHeight}
                         className={className} error={error} label={label} arrowClassName={arrowClassName}
                         errorClassName={errorClassName} itemClassName={itemClassName} submitOnClose={submitOnClose}
                         itemContainerClassName={itemContainerClassName} valueClassName={valueClassName}
                         labelClassName={labelClassName} disabled={disabled} placeholder={placeholder}/>
    );
    return (
        <DvrdSelect onChange={_onChange} onChangeSearch={onChangeSearch} value={internalValue} items={_items}
                    itemsPosition={itemsPosition} selectOnly={selectOnly} searchValue={search}
                    optionsContainerHeight={optionsContainerHeight} className={className} error={error} label={label}
                    arrowClassName={arrowClassName} errorClassName={errorClassName} itemClassName={itemClassName}
                    itemContainerClassName={itemContainerClassName} valueClassName={valueClassName}
                    labelClassName={labelClassName} disabled={disabled}/>
    );
}